import {
  createContext,
  useReducer,
  useEffect,
  useContext,
  Dispatch,
} from 'react'
import { UserActivityContext } from './index'
import { GlobalContext } from '../global'
import {
  sortCompare,
  downloadCsv,
  rateTitleFormat,
  numFixedRefillZero,
} from 'src/utils'

type Props = {
  tableList: any
  summaryLoading: boolean
  gameListObject: any
  countryListObject: any
  networkObject: any
  sort: any
  holdData: any
}

type Action = {
  type: string
  payload?: any
}
const type = {
  interstitial: 'Interstitial',
  banner: 'Banner',
  reward_video: 'Rewarded Video',
  nativeads: 'Native Ads',
  openads: 'App Open',
  rewardinter: 'Rewarded Interstitial',
  other: 'Other',
  android: 'Android',
  all: 'All',
}
const list = {
  columns: [
    { id: 'game', label: 'Summary', styles: { fontSize: '18px' } },
    { id: 'arpdau', label: 'ARPDAU' },
    { id: 'impdau', label: rateTitleFormat('Imp', 'DAU') },
    { id: 'dau', label: 'DAU' },
  ],
  rows: [],
}
type Reducer = (prevState: Props, action: Action) => Props

const initialState: Props = {
  tableList: list,
  summaryLoading: false,
  gameListObject: {},
  countryListObject: {},
  networkObject: {},
  sort: {
    id: 'revenue',
    by: null,
  },
  holdData: {},
}
const reducer: Reducer = (prevState: Props, action: Action): Props => {
  switch (action.type) {
    case 'summaryLoading':
    case 'gameListObject':
    case 'countryListObject':
    case 'networkObject':
    case 'holdData':
      return { ...prevState, [action.type]: action.payload }
    case 'tableList':
      const { tableData, view } = action.payload
      let data: any = []
      if (view === 'all' && tableData.all) {
        data = tableData.all.arpdau.days.map((item: string, index: number) => {
          return {
            key: item,
            game: item,
            icon: '',
            arpdau: numFixedRefillZero(tableData.all.arpdau.values[index]),
            dau: tableData.all.dau.values[index],
            impdau: numFixedRefillZero(tableData.all.impdau.values[index]),
          }
        })
      } else {
        data = Object.keys(tableData).map(item => {
          return {
            key: item,
            game:
              prevState.gameListObject[item]?.name ||
              prevState.countryListObject[item]?.name ||
              prevState.networkObject[item] ||
              type[item] ||
              item,
            icon: prevState.gameListObject[item]?.icon || '',
            arpdau: numFixedRefillZero(tableData[item].arpdau.summary),
            dau: tableData[item].dau.summary,
            impdau: numFixedRefillZero(tableData[item].impdau.summary),
          }
        })
      }
      return {
        ...prevState,
        sort: {
          id: 'revenue',
          by: null,
        },
        tableList: {
          columns: [
            {
              id: 'game',
              label: 'Summary',
              styles: { fontSize: '18px' },
            },
            { id: 'arpdau', label: 'ARPDAU' },
            { id: 'impdau', label: rateTitleFormat('Imp', 'DAU') },
            { id: 'dau', label: 'DAU' },
          ],
          rows: data,
        },
      }
    case 'sortTableList':
      const list = prevState.tableList
      return {
        ...prevState,
        sort: {
          id: action.payload.id,
          by: action.payload.by,
        },
        tableList: {
          columns: list.columns,
          rows: sortCompare(list.rows, action.payload.id, action.payload.by),
        },
      }
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}

type Context = {
  state: Props
  dispatch: Dispatch<Action>
  sortList: (sort: any) => void
  exportData: (data: any, allData: any) => void
}

export const SummaryContext = createContext<Context>({
  state: initialState,
  dispatch: (action: Action) => {},
  sortList: () => {},
  exportData: () => {},
})

export const SummaryContextProvider = ({
  children,
}: JSX.ElementChildrenAttribute): JSX.Element => {
  const [state, dispatch] = useReducer<Reducer>(reducer, initialState)
  const { state: dashState } = useContext(UserActivityContext)
  const { state: globalState } = useContext(GlobalContext)

  const exportData = (data: any, allData: any) => {
    const arr = Object.keys(state.holdData)
    const days = state.holdData[arr[0]].arpdau.days
    const name = `MAS_${days[0]}_${days[days.length - 1]}_${
      dashState.view
    }_view.csv`
    let csv = ''
    // 拼接筛选条件
    csv +=
      'VIEW_BY:,' +
      dashState.view +
      ',,AD_TYPE:,' +
      (dashState.type.map(item => item.label).join(',') || 'all') +
      ',,PLATFORM:,' +
      (dashState.platform.map(item => item.label).join(',') || 'all') +
      ',,APPLICATION:,' +
      (dashState.gameSelect.map(item => item.name).join(',') || 'all') +
      ',,COUNTRIES:,' +
      (dashState.country.map(item => item.name).join(',') || 'all') +
      '\n' +
      '\n'
    // 循环拼接数据
    data.forEach((column: any) => {
      csv += column.label + ','
    })
    csv += '\n'
    allData.forEach((row: any) => {
      data.forEach((column: any, i: number) => {
        const value = row[column.id] === undefined ? '' : row[column.id]
        csv += `"${(column.id === 'arpdau' ? '$' : '') + value}",`
      })
      csv += '\n'
    })
    downloadCsv(csv, name, 'text/csv;encoding:utf-8')
  }
  // effects
  useEffect(() => {
    dispatch({ type: 'gameListObject', payload: dashState.gameListObject })
    // eslint-disable-next-line
  }, [dashState.gameListObject])
  useEffect(() => {
    dispatch({
      type: 'countryListObject',
      payload: dashState.countryListObject,
    })
    // eslint-disable-next-line
  }, [dashState.countryListObject])
  useEffect(() => {
    dispatch({
      type: 'networkObject',
      payload: globalState.networkObject,
    })
  }, [globalState.networkObject])
  useEffect(() => {
    dispatch({
      type: 'tableList',
      payload: { tableData: dashState.data, view: dashState.view },
    })
    dispatch({ type: 'holdData', payload: dashState.data })
    // eslint-disable-next-line
  }, [dashState.data])
  useEffect(() => {
    dispatch({ type: 'summaryLoading', payload: dashState.dataLoading })
  }, [dashState.dataLoading])
  const sortList = (sort: any) => {
    dispatch({
      type: 'sortTableList',
      payload: sort,
    })
  }
  // returns
  return (
    <SummaryContext.Provider
      value={{
        state,
        dispatch,
        sortList,
        exportData,
      }}
    >
      {children}
    </SummaryContext.Provider>
  )
}
